import {
  AbstractBenefits,
  AbstractBenefitTier,
  AbstractBenefitPhase,
  AbstractBenefitPhaseEnum,
  PharmacyPreferencesAndFrequencies,
  PharmacyPreferences,
  PharmacyFrequencies,
  Plan,
} from "../../../@types";
import { shouldSuppressPlanBenefits } from "../../../helpers";

export interface Header {
  heading: string;
  footnoteIdx?: number;
}

export interface GeneralCost {
  brand: string;
  generic: string;
}

export interface CellData {
  isGeneric: boolean;
  cost?: string;
  generalCost?: GeneralCost;
  title?: string;
}

export interface Footnote {
  phaseName: string;
  footnote: string;
  colspan: number;
}

export interface AbstractBenefitsTableData {
  headers: Header[];
  rows: CellData[][];
  footnotes: Footnote[];
}

export const hasAbstractBenefits = (plan: Plan): boolean =>
  // eslint-disable-next-line no-prototype-builtins
  plan.hasOwnProperty("abstract_benefits") &&
  plan.abstract_benefits !== null &&
  !shouldSuppressPlanBenefits(plan);

// Valid phases contain at least one instance of non-null cost data for a particular phase and tier combination
export const getValidPhases = (
  abstractBenefits: AbstractBenefits
): string[] => {
  return Object.keys(AbstractBenefitPhaseEnum).filter((phaseName: string) => {
    return (
      abstractBenefits[phaseName] &&
      abstractBenefits[phaseName].tiers &&
      abstractBenefits[phaseName].tiers.length > 0
    );
  });
};

export const getTierNameList = (
  abstractBenefits: AbstractBenefits
): string[] => {
  const maxTiers = Math.max.apply(
    null,
    getValidPhases(abstractBenefits).map(
      phase => abstractBenefits[phase].tiers.length
    )
  );

  const maxTiersPhaseName = getValidPhases(abstractBenefits).find(
    (phaseName: string) => {
      const phase = abstractBenefits[phaseName];

      if (phase) {
        return phase.tiers && phase.tiers.length === maxTiers;
      }
      return false;
    }
  );

  if (maxTiersPhaseName) {
    const phaseWithMaxTiers: AbstractBenefitPhase | null =
      abstractBenefits[maxTiersPhaseName];

    if (phaseWithMaxTiers) {
      if (phaseWithMaxTiers.tiers) {
        return phaseWithMaxTiers.tiers.reduce(
          (refList: string[], tier: AbstractBenefitTier) => {
            refList.push(tier.label);
            return refList;
          },
          []
        );
      }
    }
  }
  return [];
};

// check all tiers in the given phase for a non empty general cost value
// all other values are ignored for that phase and will not be displayed
export const phaseHasGeneralCost = (
  abstractBenefits: AbstractBenefits | null,
  phaseName: AbstractBenefitPhaseEnum
): boolean => {
  if (abstractBenefits) {
    const phase = abstractBenefits[phaseName];

    if (phase && phase.tiers) {
      return phase.tiers.some((tier: AbstractBenefitTier) => tier.general);
    }
  }
  return false;
};

export const dropdownTranslations = {
  [PharmacyPreferencesAndFrequencies.PHARM_STD_FREQ_30_DAYS]:
    "abstract_benefits.dropdown.pharm_std_freq_30_days",
  [PharmacyPreferencesAndFrequencies.PHARM_STD_FREQ_90_DAYS]:
    "abstract_benefits.dropdown.pharm_std_freq_90_days",
  [PharmacyPreferencesAndFrequencies.PHARM_PRFD_FREQ_30_DAYS]:
    "abstract_benefits.dropdown.pharm_prfd_freq_30_days",
  [PharmacyPreferencesAndFrequencies.PHARM_PRFD_FREQ_90_DAYS]:
    "abstract_benefits.dropdown.pharm_prfd_freq_90_days",
  [PharmacyPreferencesAndFrequencies.PHARM_MAIL_STD_FREQ_30_DAYS]:
    "abstract_benefits.dropdown.pharm_mail_std_freq_30_days",
  [PharmacyPreferencesAndFrequencies.PHARM_MAIL_STD_FREQ_90_DAYS]:
    "abstract_benefits.dropdown.pharm_mail_std_freq_90_days",
  [PharmacyPreferencesAndFrequencies.PHARM_MAIL_PRFD_FREQ_30_DAYS]:
    "abstract_benefits.dropdown.pharm_mail_prfd_freq_30_days",
  [PharmacyPreferencesAndFrequencies.PHARM_MAIL_PRFD_FREQ_90_DAYS]:
    "abstract_benefits.dropdown.pharm_mail_prfd_freq_90_days",
};

// Truthy if any cost value is found to not be null for the given pharmacy preference
const hasSomeCostForPharmacyPreference = (
  pharmacyPreference: PharmacyPreferences
) => (abstractBenefits: AbstractBenefits): boolean => {
  const keys = Object.keys(abstractBenefits);
  return keys.some((phase: string) => {
    if (abstractBenefits[phase] && abstractBenefits[phase].tiers) {
      return abstractBenefits[phase].tiers.some((tier: string) => {
        return tier[pharmacyPreference];
      });
    }
    return false;
  });
};

export const hasPharmacyStandardRetail = hasSomeCostForPharmacyPreference(
  PharmacyPreferences.standard_retail
);

export const hasPharmacyPreferredRetail = hasSomeCostForPharmacyPreference(
  PharmacyPreferences.preferred_retail
);

export const hasPharmacyStandardMail = hasSomeCostForPharmacyPreference(
  PharmacyPreferences.standard_mail_order
);

export const hasPharmacyPreferredMail = hasSomeCostForPharmacyPreference(
  PharmacyPreferences.preferred_mail_order
);

export const filterOptions = (
  abstractBenefits: AbstractBenefits
): PharmacyPreferencesAndFrequencies[] =>
  Object.keys(PharmacyPreferencesAndFrequencies).filter(
    (prefAndFreq: string) => {
      switch (prefAndFreq) {
        case PharmacyPreferencesAndFrequencies.PHARM_STD_FREQ_30_DAYS:
          return hasPharmacyStandardRetail(abstractBenefits);

        case PharmacyPreferencesAndFrequencies.PHARM_PRFD_FREQ_30_DAYS:
          return hasPharmacyPreferredRetail(abstractBenefits);

        case PharmacyPreferencesAndFrequencies.PHARM_MAIL_STD_FREQ_90_DAYS:
          return hasPharmacyStandardMail(abstractBenefits);

        case PharmacyPreferencesAndFrequencies.PHARM_MAIL_PRFD_FREQ_90_DAYS:
          return hasPharmacyPreferredMail(abstractBenefits);
        default:
          return false;
      }
    }
  ) as PharmacyPreferencesAndFrequencies[];

// given a tier object, retrieve cost based on pharmacy preference and frequency
const getABCostValue = (
  tier: AbstractBenefitTier | null,
  pharmPref: PharmacyPreferences,
  freq: PharmacyFrequencies
): PharmacyFrequencies | string => {
  if (tier && tier[pharmPref]) {
    const pref = pharmPref.toString();

    if (tier && tier[pref] !== null) {
      return tier[pref][freq];
    }
  }

  return "";
};

// given a phase object and tier index, retrieve cost based on pharmacy preference and frequency
export const getCostGivenPhaseTierPref = (
  phase: AbstractBenefitPhase,
  tierName: string,
  pharmPrefAndFreq: PharmacyPreferencesAndFrequencies
): string | undefined => {
  const currentTier = phase.tiers.find(tier => tier.label === tierName);

  if (currentTier) {
    let pharmacyPreference;
    let pharmacyFrequency;

    switch (pharmPrefAndFreq) {
      case PharmacyPreferencesAndFrequencies.PHARM_STD_FREQ_30_DAYS:
        pharmacyPreference = PharmacyPreferences.standard_retail;
        pharmacyFrequency = PharmacyFrequencies.days_30;
        break;
      case PharmacyPreferencesAndFrequencies.PHARM_STD_FREQ_90_DAYS:
        pharmacyPreference = PharmacyPreferences.standard_retail;
        pharmacyFrequency = PharmacyFrequencies.days_90;
        break;
      case PharmacyPreferencesAndFrequencies.PHARM_PRFD_FREQ_30_DAYS:
        pharmacyPreference = PharmacyPreferences.preferred_retail;
        pharmacyFrequency = PharmacyFrequencies.days_30;
        break;
      case PharmacyPreferencesAndFrequencies.PHARM_PRFD_FREQ_90_DAYS:
        pharmacyPreference = PharmacyPreferences.preferred_retail;
        pharmacyFrequency = PharmacyFrequencies.days_90;
        break;
      case PharmacyPreferencesAndFrequencies.PHARM_MAIL_STD_FREQ_30_DAYS:
        pharmacyPreference = PharmacyPreferences.standard_mail_order;
        pharmacyFrequency = PharmacyFrequencies.days_30;
        break;
      case PharmacyPreferencesAndFrequencies.PHARM_MAIL_STD_FREQ_90_DAYS:
        pharmacyPreference = PharmacyPreferences.standard_mail_order;
        pharmacyFrequency = PharmacyFrequencies.days_90;
        break;
      case PharmacyPreferencesAndFrequencies.PHARM_MAIL_PRFD_FREQ_30_DAYS:
        pharmacyPreference = PharmacyPreferences.preferred_mail_order;
        pharmacyFrequency = PharmacyFrequencies.days_30;
        break;
      case PharmacyPreferencesAndFrequencies.PHARM_MAIL_PRFD_FREQ_90_DAYS:
        pharmacyPreference = PharmacyPreferences.preferred_mail_order;
        pharmacyFrequency = PharmacyFrequencies.days_90;
        break;
      default:
        return "";
    }

    return getABCostValue(currentTier, pharmacyPreference, pharmacyFrequency);
  } else {
    return "";
  }
};

enum FootnoteEnum {
  footnote_1 = "The above cost-sharing only applies to some drugs on this tier. For all other drugs, you pay 25% for generic drugs and 25% for brand-name drugs.",
  footnote_2 = "For all other drugs, you pay 25% for generic drugs and 25% for brand-name drugs.",
  footnote_1_2019 = "The above cost-sharing only applies to some drugs on this tier. For all other drugs, you pay 37% for generic drugs and 25% for brand-name drugs.",
  footnote_2_2019 = "All drugs may not be offered with additional gap coverage, for all other drugs, you pay 44% for generic drugs and 35% for brand-name drugs.",
  footnote_3_2019 = "For all other drugs, you pay 37% for generic drugs and 25% for brand-name drugs.",
}

const translateFootnote = (footnote: string): string => {
  switch (footnote as FootnoteEnum) {
    case FootnoteEnum.footnote_1:
      return "abstract_benefits.footnote_1";
    case FootnoteEnum.footnote_2:
      return "abstract_benefits.footnote_2";
    case FootnoteEnum.footnote_1_2019:
      return "abstract_benefits.2019.footnote_1";
    case FootnoteEnum.footnote_2_2019:
      return "abstract_benefits.2019.footnote_2";
    case FootnoteEnum.footnote_3_2019:
      return "abstract_benefits.2019.footnote_3";
    default:
      return footnote;
  }
};

export const scanFootnotes = (abstractBenefits: AbstractBenefits): Footnote[] =>
  getValidPhases(abstractBenefits).reduce(
    (footnotes: Footnote[], phaseName: string, _, phases) => {
      if (abstractBenefits) {
        const phase: AbstractBenefitPhase = abstractBenefits[phaseName];

        if (phase && phase.footnote) {
          return footnotes.concat([
            {
              phaseName,
              footnote: translateFootnote(phase.footnote),
              colspan: phases.length + 1,
            },
          ]);
        }
      }
      return footnotes;
    },
    []
  );

export const phaseHasFootnote = (
  footnotes: Footnote[],
  phaseName: string
): boolean => footnotes.some(footnote => footnote.phaseName === phaseName);
